import { slugify, cx } from '@/utils/strings'

import Quote from '@/components/ui/quote'
import Image from '@/components/ui/image'
import Slider from '@/components/ui/slider/slider'
import type { Entry } from 'contentful'
import type {
  TypeComponentQuoteSkeleton,
  TypeComponentTableSkeleton,
  TypeResourceImageSkeleton,
  TypeSectionSetOfCardsSkeleton,
  TypeSectionSliderSkeleton,
} from '@/types/ctf'

import PresentationSlide from '../ui/slider/presentation-slide'
import Table from '../table'
import { SetOfCardsSectionComponent } from './set-of-cards'

type SectionSlider = Entry<TypeSectionSliderSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type QuoteEntry = Entry<TypeComponentQuoteSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ImageEntry = Entry<TypeResourceImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCards = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type TableEntry = Entry<TypeComponentTableSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type SliderSectionProps = SectionSlider['fields']

export default function SliderSection({
  internalName,
  className,
  pdfPath,
  headline,
  slides,
  sliderStyle,
}: SliderSectionProps & { className?: string;  pdfPath?: string }) {
  return (
    <section id={slugify(internalName)} className={className && className}>
      {headline && <h3 className="text-center font-normal">{headline}</h3>}
      <Slider customCss="md:[--bw-slider-nav-button-distance:2.44rem]" sliderStyle={sliderStyle} pdfPath={pdfPath}>
        {slides?.map((slide, index) => {
          if (!slide) return null
          const { fields } = slide
          if (slide.sys.contentType.sys.id === 'componentQuote') {
            const quote = fields as QuoteEntry['fields']
            return (
              <div key={index} className="mx-4 md:mx-14 mb-14">
                <Quote {...quote} imageAspectRatio={1} layout="horizontal" />
              </div>
            )
          }
          if (slide.sys.contentType.sys.id === 'resourceImage') {
            const image = fields as ImageEntry['fields']
            return (
              <div
                key={index}
                className={cx(
                  'mx-14 mb-14 max-w-full h-full flex items-center justify-center',
                  !image.zoomable && 'pointer-events-none'
                )}
              >
                <Image {...image} />
              </div>
            )
          }

          if (slide.sys.contentType.sys.id === 'sectionSetOfCards') {
            const sectionCards = fields as SetOfCards['fields']

            return sliderStyle === 'Presentation' ? (
              <div key={index}>
                <PresentationSlide {...sectionCards} />
              </div>
            ) : (
              <SetOfCardsSectionComponent {...sectionCards} key={index} />
            )
          }

          if (slide.sys.contentType.sys.id === 'componentTable') {
            const tableData = fields as TableEntry['fields']

            return sliderStyle === 'Presentation' ? (
              <div key={index}>
                <PresentationSlide table={tableData} />
              </div>
            ) : (
              <div key={index}>
                <Table {...tableData} />
              </div>
            )
          }

          return null
        })}
      </Slider>
    </section>
  )
}
